/**
 * Recovery & keys settings: recovery alerts from other browsers (approve/deny with this trusted
 * device, and passkey unlocks on new browsers), the recovery kit (status, regenerate, restore),
 * passkey PRF protection (one-Face-ID unlock) and each machine's signed revocation state.
 */
import type { RecoveryRequest, UnlockRecord } from '@viko/protocol'
import { Button } from '@viko/transcript-ui'
import { Copy, KeyRound, ShieldAlert, ShieldCheck } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import {
  decideRecovery,
  machineRevocationState,
  machineUnlocks,
  type RecoveryResult,
  recoverAccess,
  recoveryAlerts,
  setupPasskeyPrf,
} from '../lib/content-recovery.js'
import { loadContentTrust } from '../lib/content-trust.js'
import { endorseDevice } from '../lib/device-key.js'
import { passkeys } from '../lib/passkeys.js'
import {
  activatePendingKit,
  type KitStatus,
  pendingKitRestore,
  type RecoveryKit,
  recoveryKitStatus,
  restoreWithKit,
  rotateRecoveryKit,
} from '../lib/recovery-kit.js'
import { KitInput, RecoveryKitCard } from './RecoveryKit.js'
import { errorText, useToast } from './ui.js'

const when = (ts: number) => new Date(ts).toLocaleString()

export function RecoveryAlertsView({
  requests,
  onDecide,
}: {
  requests: RecoveryRequest[]
  onDecide: (id: string, decision: 'approve' | 'deny') => void
}) {
  if (!requests.length) return null
  return (
    <div role="alert" className="mx-4 mb-3 grid gap-2 rounded-lg border border-border-subtle p-3">
      {requests.map((r) =>
        r.status === 'completed' ? (
          <div key={r.id} className="flex flex-wrap items-center gap-2">
            <ShieldAlert size={16} aria-hidden="true" className="text-status-wait" />
            <span className="min-w-0 flex-1 text-[15px]">
              A passkey unlocked your encrypted work on a new browser at {when(r.createdAt)}. Not
              you? Use <strong className="font-semibold">Lost device</strong> in Devices to revoke
              that passkey.
            </span>
          </div>
        ) : (
          <div key={r.id} className="flex flex-wrap items-center gap-2">
            <ShieldAlert size={16} aria-hidden="true" className="text-status-fail" />
            <span className="min-w-0 flex-1 text-[15px]">
              {r.kind === 'recovery' ? 'Recovery code' : 'Passkey'} recovery requested on another
              browser.{' '}
              {r.status === 'held'
                ? `Keys are held until ${when(r.availableAt)}.`
                : 'Keys are available to that browser now.'}
            </span>
            <Button size="sm" variant="danger" onClick={() => onDecide(r.id, 'deny')}>
              Deny
            </Button>
            {r.status === 'held' ? (
              <Button size="sm" onClick={() => onDecide(r.id, 'approve')}>
                Approve
              </Button>
            ) : null}
          </div>
        ),
      )}
    </div>
  )
}

/**
 * unlock-via-mac: every signed-in device sees which machine unlocked which browser in the last
 * day (the Mac only does so for a trusted browser with a fresh passkey tap). Not you? Lost device.
 */
export function MachineUnlocksView({ unlocks }: { unlocks: UnlockRecord[] }) {
  if (!unlocks.length) return null
  return (
    <ul className="mx-4 mb-3 grid list-none gap-2 rounded-lg border border-border-subtle p-3">
      {unlocks.map((u) => (
        <li key={u.requestId} className="flex items-start gap-2 text-[15px]">
          <ShieldCheck size={16} aria-hidden="true" className="mt-0.5 flex-none text-text-3" />
          <span className="min-w-0 flex-1">
            Unlocked via {u.machineName ?? 'a machine'} on {u.deviceLabel ?? 'a browser'} at{' '}
            {when(u.decidedAt ?? u.createdAt)}. Not you? Use{' '}
            <strong className="font-semibold">Lost device</strong> in Devices.
          </span>
        </li>
      ))}
    </ul>
  )
}

export function RecoveryCodeView({ code, onDone }: { code: string; onDone: () => void }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="mx-4 mb-3 grid gap-2 rounded-lg bg-surface-2 p-3">
      <p className="m-0 text-[14px] text-text-2">
        Save this recovery code somewhere safe. It is shown once, never sent to Viko, and it is the
        only way back if you lose every device and passkey.
      </p>
      <output className="break-all font-mono text-[15px]" aria-label="Recovery code">
        {code}
      </output>
      <div className="flex gap-2">
        <Button
          size="sm"
          icon={<Copy size={14} />}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(code)
              setCopied(true)
            } catch {
              setCopied(false)
            }
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button size="sm" variant="primary" onClick={onDone}>
          I saved it
        </Button>
      </div>
    </div>
  )
}

export function RecoveryKeys() {
  const b = useBackend()
  const toast = useToast()
  const session = b.content
  const [alerts, setAlerts] = useState<RecoveryRequest[]>([])
  const [unlocks, setUnlocks] = useState<UnlockRecord[]>([])
  const [kit, setKit] = useState<RecoveryKit>()
  const [kitStatus, setKitStatus] = useState<KitStatus>('unknown')
  const [restoring, setRestoring] = useState<string>()
  const [busy, setBusy] = useState(false)
  const [held, setHeld] = useState<RecoveryResult | undefined>()
  const [machines, setMachines] = useState<
    { machineId: string; revocationSeq: number; epoch: number; verified: boolean }[]
  >([])
  const [seq, setSeq] = useState<number>()

  const load = useCallback(async () => {
    if (!session) return
    try {
      const accountId = await session.accountId()
      setKitStatus(await recoveryKitStatus(session))
      setRestoring(await pendingKitRestore(accountId))
      setAlerts(await recoveryAlerts(b.api))
      setUnlocks(await machineUnlocks(b.api).catch(() => []))
      setMachines(await machineRevocationState(b.api, accountId))
      setSeq((await loadContentTrust(accountId).catch(() => undefined))?.revocationSeq)
    } catch {
      // Older relays or locked sessions: the section stays usable for recovery.
    }
  }, [b, session])
  useEffect(() => {
    void load()
  }, [load])

  if (!session) return null
  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    try {
      await fn()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
      void load()
    }
  }
  const endorse = () => endorseDevice(passkeys, (e) => b.api.registerDevice(e))
  const recover = (input: { kind: 'recovery'; code: string } | { kind: 'prf' }) =>
    run(async () => {
      const result =
        input.kind === 'recovery'
          ? await restoreWithKit(session, b.api, input.code, { endorse })
          : await recoverAccess(session, input, { endorse })
      setHeld(result.status === 'held' ? result : undefined)
      if (result.status === 'recovered') toast('This browser can read your encrypted work again')
      else if (result.status === 'unsupported')
        toast('This passkey did not return a PRF secret; use your recovery kit', 'error')
    })
  const kitLine: Record<KitStatus, string> = {
    active: 'Active. It can recover your account and encrypted work.',
    pending:
      'Saved, but not active yet: it activates when you approve your first machine, or here.',
    missing: 'No data recovery key yet. Losing every device would lose your encrypted work.',
    unknown: 'Unlock encrypted content on this browser to see its state.',
  }

  return (
    <>
      <MachineUnlocksView unlocks={unlocks} />
      <RecoveryAlertsView
        requests={alerts}
        onDecide={(id, decision) =>
          void run(async () => {
            await decideRecovery(session, b.api, id, decision)
            toast(decision === 'approve' ? 'Recovery approved' : 'Recovery denied')
          })
        }
      />
      <div className="grid gap-1 px-4 pb-3">
        <h3 className="m-0 text-[15px] font-semibold">Recovery kit</h3>
        <p role="status" className="m-0 text-[14px] text-text-2">
          {kitLine[kitStatus]}
        </p>
      </div>
      {kit ? (
        <div className="mx-4 mb-3 rounded-lg bg-surface-2 p-3">
          <RecoveryKitCard kit={kit} onDone={() => setKit(undefined)} doneLabel="I saved it" />
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2 px-4 pb-3">
        {kitStatus === 'pending' ? (
          <Button
            icon={<KeyRound size={15} />}
            disabled={busy}
            onClick={() =>
              run(async () => {
                await activatePendingKit(session)
                toast('Your recovery kit is active')
              })
            }
          >
            Activate kit
          </Button>
        ) : null}
        <Button
          icon={<KeyRound size={15} />}
          disabled={busy}
          onClick={() => {
            if (
              kitStatus === 'active' &&
              !confirm(
                'Make a new recovery kit? Your current kit stops working: its codes and data recovery key are replaced.',
              )
            )
              return
            void run(async () => setKit(await rotateRecoveryKit(session, b.api, passkeys)))
          }}
        >
          {kitStatus === 'active' ? 'Regenerate kit' : 'Create recovery kit'}
        </Button>
        <Button
          icon={<ShieldCheck size={15} />}
          disabled={busy}
          onClick={() =>
            run(async () => {
              const r = await setupPasskeyPrf(session)
              toast(
                r.supported
                  ? 'Your passkey can now recover encrypted work'
                  : 'This passkey does not support PRF; keep a recovery code instead',
                r.supported ? 'ok' : 'error',
              )
            })
          }
        >
          Protect with passkey
        </Button>
      </div>
      <details className="px-4 pb-3" open={!!restoring}>
        <summary className="cursor-pointer text-[15px] text-accent-text">
          {restoring
            ? 'Finish restoring encrypted work on this browser'
            : 'Restore encrypted work on this browser'}
        </summary>
        <div className="mt-2 grid gap-2">
          <KitInput
            busy={busy}
            submitLabel="Restore from this kit"
            onKit={(k) => {
              if (!k.dataKey) toast('This kit has no data recovery key', 'error')
              else void recover({ kind: 'recovery', code: k.dataKey })
            }}
          />
          <Button variant="ghost" disabled={busy} onClick={() => void recover({ kind: 'prf' })}>
            Use passkey instead
          </Button>
        </div>
      </details>
      {held?.status === 'held' ? (
        <p role="status" className="m-0 px-4 pb-3 text-[14px] text-text-2">
          For your safety, keys are released {when(held.request.availableAt)} unless another trusted
          device approves sooner. Every signed-in device was alerted.
        </p>
      ) : null}
      {machines.length ? (
        <ul className="m-0 list-none p-0" aria-label="Machine key state">
          {machines.map((m) => (
            <li key={m.machineId} className="row !cursor-default">
              <span className="min-w-0 flex-1 text-[15px]">{m.machineId}</span>
              <span className="row__meta">
                {!m.verified
                  ? 'Unverified report'
                  : seq !== undefined && m.revocationSeq >= seq
                    ? `Up to date · key epoch ${m.epoch}`
                    : 'Catching up on revocations'}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  )
}
